import { DefineComponentProps } from 'utils'
import classNames from 'classnames'

interface VirtualListData extends Record<string, any> {}

interface VirtualListProps {
  itemMinHeight: number
  data: VirtualListData[]
  noRenderWhenNotInView?: boolean
  rowKey?: string
  isLazyLoad?: boolean
  className?: string
  root?: HTMLElement
}

export type VirtualListExpose = {
  resetRenderStatus: () => void
}

export const VirtualList = defineComponent<VirtualListProps>({
  props: DefineComponentProps<VirtualListProps>(['itemMinHeight', 'data', 'noRenderWhenNotInView', 'rowKey', 'isLazyLoad', 'className', 'root']),
  setup(props, { slots, expose }) {
    const { noRenderWhenNotInView = true } = props
    const observer = ref<IntersectionObserver>()
    const virtualListRef = ref<HTMLDivElement>()
    const isRenderMap = ref<Record<string, boolean>>({})
    const isObserveMap = ref<Record<string, boolean>>({})

    const setIsRenderMap = (key: string, value: boolean) => {
      if (!props.isLazyLoad) {
        isRenderMap.value[key] = value
        return
      }
      isRenderMap.value[key] = value
    }

    const initObserver = () => {
      observer.value = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const target = entry.target as HTMLDivElement
            const index = target.dataset.index
            if (!index) return
            if (entry.isIntersecting) {
              setIsRenderMap(index, true)
            } else {
              if (noRenderWhenNotInView) {
                isRenderMap.value[index] = false
              }
            }
          })
        },
        {
          rootMargin: '240px 0px',
          root: props.root,
        },
      )
    }

    // 初始化时检查已经在可视区域内的元素
    const initVisibleItems = () => {
      if (!virtualListRef.value || !observer.value) return

      const children = virtualListRef.value.querySelectorAll('.virtual-list-item-wrapper')
      children.forEach((child) => {
        const index = (child as HTMLDivElement).dataset.index
        if (!index) return
        if (isObserveMap.value[index]) return

        // // 手动检查元素是否在可视区域内
        // const rect = child.getBoundingClientRect()
        // const isVisible = rect.top < window.innerHeight && rect.bottom > 0

        // if (isVisible) {
        //   setIsRenderMap(index, true)
        // }
        isObserveMap.value[index] = true
        // 开始观察元素
        observer.value?.observe(child)
      })
    }

    onBeforeMount(() => {
      initObserver()
    })

    expose({
      resetRenderStatus: () => {
        isRenderMap.value = {}
      },
    })

    onMounted(() => {
      nextTick(() => {
        initVisibleItems()
      })
    })

    const disposeObserver = () => {
      if (virtualListRef.value) {
        const children = virtualListRef.value.querySelectorAll('.virtual-list-item-wrapper')
        if (children.length) {
          children.forEach((child) => {
            observer.value?.unobserve(child)
          })
        }
        observer.value?.disconnect()
        isObserveMap.value = {}
        observer.value = undefined
      }
    }

    onBeforeUnmount(() => {
      disposeObserver()
    })

    const handleObserverMap = (newData: VirtualListData[], oldData: VirtualListData[]) => {
      if (newData.length < oldData.length) {
        const startIndex = newData.length
        for (let i = startIndex; i < oldData.length; i++) {
          isRenderMap.value[i] = false
        }
      }
    }

    watch(
      () => props.data,
      (newData, oldData) => {
        if (newData.length !== oldData?.length) {
          handleObserverMap(newData, oldData || [])
          disposeObserver()
          initObserver()
          nextTick(() => {
            initVisibleItems()
          })
        }
      },
      {
        deep: true,
        immediate: true,
      },
    )

    return () => {
      return (
        <div class={classNames('virtual-list', props.className)}>
          <div class="virtual-list-wrapper" ref={virtualListRef}>
            {props.data.map((item, index) => {
              const key = index
              return (
                <div
                  class={classNames('virtual-list-item-wrapper', { 'is-render': isRenderMap.value[key] })}
                  key={key}
                  style={{ minHeight: `${!isRenderMap.value[key] ? props.itemMinHeight : 0}px` }}
                  data-index={key}
                >
                  {isRenderMap.value[key] ? (
                    <div class="virtual-list-cell" key={item.id}>
                      {slots.default?.({ data: item, index })}
                    </div>
                  ) : (
                    slots.placeholder?.({ data: item, index })
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )
    }
  },
})
